import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';

const BulkDeleteWithColumnsModal = ({
  open,
  onClose,
  dashboardData = [],
  onConfirmDelete,
  isLoading = false,
}) => {
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [validationState, setValidationState] = useState({
    isValidating: false,
    progress: 0,
    validSites: [],
    invalidSites: [],
    isComplete: false,
  });
  const [deleteProgress, setDeleteProgress] = useState({
    isDeleting: false,
    completed: 0,
    total: 0,
    successful: [],
    failed: [],
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileData([]);
      setValidationState({
        isValidating: false,
        progress: 0,
        validSites: [],
        invalidSites: [],
        isComplete: false,
      });
      setDeleteProgress({
        isDeleting: false,
        completed: 0,
        total: 0,
        successful: [],
        failed: [],
      });
    }
  }, [open]);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
      alert('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
      return;
    }

    setFile(selectedFile);
    readFile(selectedFile);
  };

  const readFile = (fileToRead) => {
    const fileExtension = fileToRead.name.split('.').pop().toLowerCase();

    if (fileExtension === 'csv') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          // Use XLSX to parse CSV for better handling of quoted values
          const workbook = XLSX.read(text, { type: 'string', sheetRows: 0 });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
          });

          if (jsonData.length < 2) {
            alert('CSV file is empty or invalid.');
            return;
          }

          // Convert array format to object format
          const headers = jsonData[0];
          const dataRows = jsonData.slice(1);

          const result = dataRows.map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] || '';
            });
            return obj;
          });

          setFileData(result);
        } catch (error) {
          console.error('Error reading CSV file:', error);
          alert('Error reading CSV file. Please check the file format.');
        }
      };
      reader.readAsText(fileToRead);
    } else {
      // Excel file
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
          });

          if (jsonData.length < 2) {
            alert('Excel file is empty or invalid.');
            return;
          }

          // Convert array format to object format
          const headers = jsonData[0];
          const dataRows = jsonData.slice(1);

          const result = dataRows.map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] || '';
            });
            return obj;
          });

          setFileData(result);
        } catch (error) {
          console.error('Error reading Excel file:', error);
          alert('Error reading Excel file. Please check the file format.');
        }
      };
      reader.readAsArrayBuffer(fileToRead);
    }
  };

  const findSiteCodeColumn = (row) => {
    const possibleKeys = [
      'site_code',
      'Site_Code',
      'Site Code',
      'site_id',
      'Site_ID',
      'Site ID',
      'sitecode',
      'SiteCode',
      'SITECODE',
      'siteid',
      'SiteId',
      'SITEID',
    ];

    for (const key of possibleKeys) {
      if (row.hasOwnProperty(key)) {
        return key;
      }
    }

    // Also check case-insensitive match
    const rowKeys = Object.keys(row);
    for (const possibleKey of possibleKeys) {
      const found = rowKeys.find((rowKey) => rowKey.toLowerCase() === possibleKey.toLowerCase());
      if (found) {
        return found;
      }
    }

    return null;
  };

  const validateSiteCodes = async () => {
    if (fileData.length === 0) {
      alert('Please upload a file first.');
      return;
    }

    setValidationState({
      isValidating: true,
      progress: 0,
      validSites: [],
      invalidSites: [],
      isComplete: false,
    });

    const validSites = [];
    const invalidSites = [];
    const totalRows = fileData.length;

    // dashboardData from /getFiberSiteDashboard already contains only FTTS sites
    // Create a map of all sites for efficient lookup
    const sitesMap = new Map();
    dashboardData.forEach((site) => {
      // Check all possible site code fields
      const siteCodeValue =
        site.site_code ||
        site.Site_Code ||
        site['Site Code'] ||
        site.site_id ||
        site.Site_ID ||
        site['Site ID'] ||
        '';
      if (siteCodeValue) {
        // Normalize the site code: trim and convert to lowercase
        const normalizedKey = siteCodeValue.toString().trim().toLowerCase().replace(/\s+/g, '');
        if (!sitesMap.has(normalizedKey)) {
          sitesMap.set(normalizedKey, site);
        }
      }
    });

    // Debug logging - log first few site codes to verify
    const sampleSiteCodes = Array.from(sitesMap.keys()).slice(0, 10);
    console.log('Validation Debug Info:', {
      totalDashboardSites: dashboardData.length,
      fileDataRows: fileData.length,
      sitesMapSize: sitesMap.size,
      sampleSiteCodes: sampleSiteCodes,
    });

    for (let i = 0; i < fileData.length; i++) {
      const row = fileData[i];
      const siteCodeKey = findSiteCodeColumn(row);

      if (!siteCodeKey) {
        invalidSites.push({
          siteCode: 'N/A',
          reason: 'Site code column not found in row',
          rowNumber: i + 2, // +2 because of header and 0-index
        });
        continue;
      }

      const siteCode = row[siteCodeKey];
      if (!siteCode || siteCode.toString().trim() === '') {
        invalidSites.push({
          siteCode: 'N/A',
          reason: 'Site code is empty',
          rowNumber: i + 2,
        });
        continue;
      }

      // Find matching site (case-insensitive, normalize whitespace)
      const siteCodeStr = siteCode.toString().trim();
      // Normalize: remove all whitespace and convert to lowercase
      const normalizedSiteCode = siteCodeStr.replace(/\s+/g, '').toLowerCase();
      let matchingSite = sitesMap.get(normalizedSiteCode);

      // If not found in map, try direct search in dashboardData (fallback)
      if (!matchingSite) {
        matchingSite = dashboardData.find((site) => {
          // Check all possible site code fields
          const possibleSiteCodes = [
            site.site_code,
            site.Site_Code,
            site['Site Code'],
            site.site_id,
            site.Site_ID,
            site['Site ID'],
          ].filter(Boolean); // Remove null/undefined values

          return possibleSiteCodes.some((code) => {
            if (!code) {
              return false;
            }
            const normalized = code.toString().trim().replace(/\s+/g, '').toLowerCase();
            return normalized === normalizedSiteCode;
          });
        });
      }

      if (matchingSite && matchingSite.id) {
        validSites.push({
          id: matchingSite.id,
          siteCode: matchingSite.site_code || siteCode,
          siteName: matchingSite.fiber_site_name || matchingSite.site_code,
        });
      } else {
        // Enhanced debug logging
        console.log(`Site "${siteCodeStr}" (normalized: "${normalizedSiteCode}") not found.`, {
          searchingFor: normalizedSiteCode,
          sitesMapHas: sitesMap.has(normalizedSiteCode),
          totalSitesInMap: sitesMap.size,
          totalDashboardSites: dashboardData.length,
          // Check if similar site codes exist (for debugging)
          similarCodes: Array.from(sitesMap.keys())
            .filter((key) =>
              key.includes(normalizedSiteCode.substring(0, Math.min(3, normalizedSiteCode.length)))
            )
            .slice(0, 5),
          // Sample of actual site codes in dashboard for comparison
          sampleDashboardSiteCodes: dashboardData
            .slice(0, 5)
            .map((s) => s.site_code || s.Site_Code || 'N/A'),
        });

        invalidSites.push({
          siteCode: siteCode.toString(),
          reason: 'Site code not found in FTTS dataset',
          rowNumber: i + 2,
        });
      }

      // Update progress
      const progress = Math.round(((i + 1) / totalRows) * 100);
      setValidationState((prev) => ({
        ...prev,
        progress,
        validSites: [...validSites],
        invalidSites: [...invalidSites],
      }));
    }

    setValidationState((prev) => ({
      ...prev,
      isValidating: false,
      progress: 100,
      isComplete: true,
    }));
  };

  const handleDelete = async () => {
    if (validationState.validSites.length === 0) {
      alert('No valid sites to delete.');
      return;
    }

    const siteIds = validationState.validSites.map((site) => site.id);
    setDeleteProgress({
      isDeleting: true,
      completed: 0,
      total: siteIds.length,
      successful: [],
      failed: [],
    });

    try {
      await onConfirmDelete(siteIds, (progress) => {
        setDeleteProgress((prev) => ({
          ...prev,
          ...progress,
        }));
      });
    } catch (error) {
      console.error('Error during bulk delete:', error);
      setDeleteProgress((prev) => ({
        ...prev,
        isDeleting: false,
      }));
    } finally {
      setDeleteProgress((prev) => ({
        ...prev,
        isDeleting: false,
      }));
    }
  };

  const canClose = !validationState.isValidating && !deleteProgress.isDeleting;

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minHeight: '500px',
        },
      }}
      BackdropProps={{
        style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
      }}
      disableEscapeKeyDown={!canClose}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#344054' }}>
          Bulk Delete (with Columns)
        </Typography>
        {canClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: '#667085',
              '&:hover': { backgroundColor: '#F5F5F5' },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* File Upload Section */}
        {!validationState.isComplete && !deleteProgress.isDeleting && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
              Upload an Excel (.xlsx, .xls) or CSV (.csv) file containing site_code column. All
              sites in the table are FTTS sites and will be validated.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Button variant="outlined" component="label" size="small">
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  disabled={validationState.isValidating}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ color: '#344054' }}>
                  {file.name}
                </Typography>
              )}
            </Box>

            {fileData.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                File loaded: {fileData.length} row(s) found
              </Alert>
            )}

            {fileData.length > 0 && !validationState.isValidating && (
              <Button
                variant="contained"
                onClick={validateSiteCodes}
                sx={{
                  backgroundColor: '#BD1D23',
                  '&:hover': { backgroundColor: '#A0151A' },
                }}
              >
                Validate Site Codes
              </Button>
            )}
          </Box>
        )}

        {/* Validation Progress */}
        {validationState.isValidating && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Validating Site Codes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {validationState.progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={validationState.progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Validation Summary */}
        {validationState.isComplete && !deleteProgress.isDeleting && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Validation Complete
              </Typography>
              <Typography variant="body2">
                Valid FTTS Sites: {validationState.validSites.length} | Invalid/Non-existing:{' '}
                {validationState.invalidSites.length}
              </Typography>
            </Alert>

            {/* Valid Sites Table */}
            {validationState.validSites.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#344054' }}>
                  Valid FTTS Sites ({validationState.validSites.length})
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Site Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Site Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationState.validSites.map((site, index) => (
                        <TableRow key={index}>
                          <TableCell>{site.siteCode}</TableCell>
                          <TableCell>{site.siteName}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Invalid Sites Table */}
            {validationState.invalidSites.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#344054' }}>
                  Invalid/Non-existing Sites ({validationState.invalidSites.length})
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Site Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Row #</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationState.invalidSites.map((site, index) => (
                        <TableRow key={index}>
                          <TableCell>{site.siteCode}</TableCell>
                          <TableCell>
                            <Chip
                              label={site.reason}
                              size="small"
                              color="error"
                              sx={{ fontSize: '11px', height: '20px' }}
                            />
                          </TableCell>
                          <TableCell>{site.rowNumber}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Delete Progress */}
        {deleteProgress.isDeleting && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Deleting Sites
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteProgress.completed} / {deleteProgress.total}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                deleteProgress.total > 0
                  ? (deleteProgress.completed / deleteProgress.total) * 100
                  : 0
              }
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Delete Summary */}
        {!deleteProgress.isDeleting && deleteProgress.completed > 0 && (
          <Box sx={{ mb: 3 }}>
            <Alert
              severity={deleteProgress.failed.length === 0 ? 'success' : 'warning'}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Delete Complete!
              </Typography>
              <Typography variant="body2">
                ✅ Successful: {deleteProgress.successful.length} | ❌ Failed:{' '}
                {deleteProgress.failed.length}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #E0E0E0' }}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
          {validationState.isComplete &&
            !deleteProgress.isDeleting &&
            deleteProgress.completed === 0 && (
              <>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    color: '#667085',
                    borderColor: '#D0D5DD',
                    '&:hover': {
                      borderColor: '#98A2B3',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={validationState.validSites.length === 0 || isLoading}
                  variant="contained"
                  sx={{
                    backgroundColor: '#BD1D23',
                    '&:hover': { backgroundColor: '#A0151A' },
                    '&:disabled': {
                      backgroundColor: '#D0D5DD',
                      color: '#98A2B3',
                    },
                  }}
                >
                  Confirm Delete
                </Button>
              </>
            )}
          {deleteProgress.completed > 0 && !deleteProgress.isDeleting && (
            <Button
              onClick={onClose}
              variant="contained"
              sx={{
                backgroundColor: '#BD1D23',
                '&:hover': { backgroundColor: '#A0151A' },
              }}
            >
              Close
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteWithColumnsModal;
